import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import type { OfflineStoreAdminView, OfflineStoreRequest, CmsPage } from '../types/cms';

const BASE = '/admin/v1/offline-stores';

const fetchStores = async (page: number, size: number): Promise<CmsPage<OfflineStoreAdminView>> => {
  const q = new URLSearchParams({ page: String(page), size: String(size), sort: 'sortOrder,asc' });
  const res = await adminFetch(`${BASE}?${q}`);
  if (!res.ok) throw new Error(`offline-stores list failed: ${res.status}`);
  const env = (await res.json()) as { data?: OfflineStoreAdminView[] | CmsPage<OfflineStoreAdminView> };
  const raw = env.data;
  if (Array.isArray(raw)) return { content: raw, totalElements: raw.length, totalPages: 1, number: 0, size };
  return raw ?? { content: [], totalElements: 0, totalPages: 0, number: 0, size };
};

// M-1: BE(catalog AdminCmsController)에 GET /admin/v1/offline-stores/{id} 단건 엔드포인트가
// 없어(list/create/update/delete 만 존재) /{id} GET 은 HttpRequestMethodNotSupported→500 이 된다.
// 목록 엔드포인트가 편집에 필요한 모든 필드(id/storeKey/name/address/hoursLabel/thumbnailUrl/
// href/isActive/sortOrder)를 그대로 반환하므로, 단건은 목록에서 추려 온다(BE 변경 의존 제거).
const STORE_PAGE_SIZE = 200;

const fetchStore = async (id: number): Promise<OfflineStoreAdminView> => {
  const page = await fetchStores(0, STORE_PAGE_SIZE);
  const found = page.content.find((s) => s.id === id);
  if (!found) throw new Error(`offline-store ${id} not found`);
  return found;
};

/**
 * QA-409 — 정렬 순서는 관리자가 입력하지 않는다. 새 매장은 기존 최대값 + 10(등록 순 일련번호)으로 저장하고,
 * 수정 때는 기존 값을 그대로 둔다. 목록·FE 노출 모두 sortOrder 오름차순이라 등록 순으로 보인다.
 */
export const SORT_ORDER_STEP = 10;
export const fetchNextStoreSortOrder = async (): Promise<number> => {
  const page = await fetchStores(0, STORE_PAGE_SIZE);
  const max = page.content.reduce((m, s) => Math.max(m, Number(s.sortOrder) || 0), 0);
  return max + SORT_ORDER_STEP;
};

export const offlineStoresQueryKey =(page: number, size: number) =>
  ['admin', 'offline-stores', page, size] as const;

export const offlineStoreQueryKey = (id: number) =>
  ['admin', 'offline-store', id] as const;

export const useOfflineStoresQuery = (page = 0, size = 20) =>
  useQuery({ queryKey: offlineStoresQueryKey(page, size), queryFn: () => fetchStores(page, size) });

export const useOfflineStoreQuery = (id: number) =>
  useQuery({ queryKey: offlineStoreQueryKey(id), queryFn: () => fetchStore(id), enabled: id > 0 });

export const useOfflineStoreMutation = () => {
  const qc = useQueryClient();
  // 수정 후 다시 열면 이전 값이 보이던 문제 — 목록 키만 무효화하고 단건 키(['admin', 'offline-store', id])는 그대로라
  // staleTime(30초) 안에는 캐시된 옛 값이 그대로 폼에 들어갔다. 단건 캐시도 함께 무효화한다.
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['admin', 'offline-stores'] }),
      qc.invalidateQueries({ queryKey: ['admin', 'offline-store'] }),
    ]);

  const create = useMutation({
    mutationFn: async (body: OfflineStoreRequest) => {
      const res = await adminFetch(BASE, { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: OfflineStoreRequest }) => {
      const res = await adminFetch(`${BASE}/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await adminFetch(`${BASE}/${id}`, { method: 'DELETE',  });
      if (!res.ok) throw new Error(`delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
